/** @file       AccountDropdown.jsx
 *  @author     Alex Huctwith <alex@kingsds.network>
 *              Arnab Sagar   <arnab@kingsds.network>
 *
 *  @date       April 2022
 *
 *              A component which displays the "more" pane for an account in the accounts list
 *              defaults to hidden and is shown by more carat button on account widget
 *              Props:
 *                - account: the keystore object for the account
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import {
  ChangeAccountPasswordModal,
  DeleteAccountModal,
  DownloadKeystoreModal,
  RenameAccountModal,
} from '../../../Modals';

import './AccountDropdown.css';

import dropdownIcon from './dropdown.png';

/**
 * Renders a component which displays the "more" pane for an account in the accounts list.
 *
 * @param   {object}      props         Component properties.
 * @param   {object}      props.account The account to display options for
 * @returns {JSX.Element}               The component.
 */
export function AccountDropdown({ account, onOpenChanged })
{
  const { t } = useTranslation();
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  return (
    <DropdownMenu.Root className="accountMore" onOpenChange={onOpenChanged}>
      <DropdownMenu.Trigger className="optionsButton">
        <img
          src={dropdownIcon}
          className="dropdownIcon"
          alt="Account Options"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="accountMoreOptions"
        align="end"
        alignOffset={25}
        // Allow modals opened from this component to control the focus.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenu.Item onSelect={() => setIsRenameModalOpen(true)}>
          {t('rename')}
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => setIsChangePasswordModalOpen(true)}>
          {t('new_password')}
        </DropdownMenu.Item>
        <DropdownMenu.Item id="accountDeleteAction" onSelect={() => setIsDeleteModalOpen(true)}>
          {t('delete')}
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => setIsDownloadModalOpen(true)}>
          {t('download')}
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => {
          // TODO(willpringle): remove when node-oauth lands, temporary keystore clipboard copy
          const objKeystore = account.toJSON();
          const leanKeystore = {
            address: objKeystore.address,
            crypto:  objKeystore.crypto,
            version: objKeystore.version,
          };
          return navigator.clipboard.writeText(JSON.stringify(leanKeystore));
        }}>
          {t('copy')}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
      <RenameAccountModal
        isOpen={isRenameModalOpen}
        closeModal={() => setIsRenameModalOpen(false)}
        account={account}
      />
      <ChangeAccountPasswordModal
        isOpen={isChangePasswordModalOpen}
        closeModal={() => setIsChangePasswordModalOpen(false)}
        account={account}
      />
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        account={account}
      />
      <DownloadKeystoreModal
        isOpen={isDownloadModalOpen}
        closeModal={() => setIsDownloadModalOpen(false)}
        account={account}
      />
    </DropdownMenu.Root>
  );
}
